// Shared row-identity lookup used by both the allocation tool's
// virtual-grouping unwrap (`useAllocationQuery`) and the trend tool's
// series-response transform (`transformSeriesResponse`).
//
// Identity semantics (intentionally strict):
//
//   - When `groupEntityId` is non-null, match ONLY by groupEntityId.
//     Do not fall back to name -- the caller asked for a specific
//     numeric identity, and falling back across siblings that share a
//     display name (e.g. "Cash" appearing under two custodians) would
//     silently pick the wrong row.
//
//   - When `groupEntityId` is null but `name` is non-null, match by
//     name. This is the fallback path for virtual groupings where
//     Orion doesn't surface a stable groupEntityId.
//
//   - When both are null, return null. Callers should always provide
//     at least one identity field.
//
// Returns null when nothing matches.

function compareIdentity (node, identity) {
  if (identity.groupEntityId != null) {
    return node?.groupEntityId === identity.groupEntityId
  }
  if (identity.name != null) {
    return node?.name === identity.name
  }
  return false
}

/**
 * Linear-search a flat array of rows for the row matching `identity`.
 * @param {Array} rows
 * @param {{ groupEntityId?: number|null, name?: string|null }} identity
 * @returns matched row or null
 */
export function findRowByIdentity (rows, identity) {
  if (!Array.isArray(rows) || !identity) return null
  if (identity.groupEntityId == null && identity.name == null) return null
  for (const row of rows) {
    if (compareIdentity(row, identity)) return row
  }
  return null
}

/**
 * Tree-walk variant: recursively descend into each node's
 * `calculations` array looking for a `calculated-group` node whose
 * identity matches. Used by the trend transform to find the wrapper
 * group containing a row's series among siblings returned by a
 * `string-equal` filter (which doesn't actually constrain Orion's
 * output -- the wrapper still returns every sibling group).
 *
 * @param {object} node -- a node or wrapper-shaped { calculations: [...] }
 * @param {{ groupEntityId?: number|null, name?: string|null }} identity
 * @returns the first matching calculated-group, or null
 */
export function findGroupByIdentityInTree (node, identity) {
  if (!node || typeof node !== 'object' || !identity) return null
  if (identity.groupEntityId == null && identity.name == null) return null
  if (node.$type === 'calculated-group' && compareIdentity(node, identity)) {
    return node
  }
  if (Array.isArray(node.calculations)) {
    for (const child of node.calculations) {
      const hit = findGroupByIdentityInTree(child, identity)
      if (hit) return hit
    }
  }
  return null
}
